import { z } from 'zod';

import { customAttributeOptionsSchema, customAttributesSchema } from './custom-attribute';
import { idSchema } from './id';
import { paginationSchema } from './pagination';
import { seniorityLevelSchema } from './seniority-level';
import { skillSchema } from './skill';

import type { Mutation } from './common';
import type { CourseHistoryMutation } from './course-history';
import type { CustomAttributes } from './custom-attribute';
import type { EducationHistoryMutation } from './education-history';
import type { ProjectHistoryMutation } from './project-history';
import type { PublicationHistoryMutation } from './publication-history';
import type { HardSoftSkill, LanguageSkill } from './skill';
import type { WorkHistoryMutation } from './work-history';

export const relocationPreferenceSchema = z.union([
    z.literal('not open'),
    z.literal('open'),
    z.literal('relocation only'),
]);

export type RelocationPreference = z.infer<typeof relocationPreferenceSchema>;
export const jobSeekingStatusSchema = z.union([z.literal('not open'), z.literal('open')]);
export type JobSeekingStatus = z.infer<typeof jobSeekingStatusSchema>;

export const baseUserSchema = z.object({
    id: z.string(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
});

export type BaseUser = z.infer<typeof baseUserSchema>;

export const contactUserSchema = baseUserSchema.extend({
    emailAddress: z.string().optional(),
});

export const extendedUserSchema = baseUserSchema.extend({
    emailAddress: z.string().optional(),
});

export const userJobProfileSchema = z.object({
    id: z.string(),
    matchingScore: z.number().optional(),
    title: z.string(),
});

export const userSchema = extendedUserSchema.extend({
    aboutMe: z.string().optional(),
    custom: customAttributesSchema.optional(),
    department: z.string().optional(),
    jobProfile: userJobProfileSchema.optional(),
    jobSeeking: jobSeekingStatusSchema.optional(),
    jobTitle: z.string().optional(),
    phoneNumber: z.string().optional(),
    relocationPreference: relocationPreferenceSchema.optional(),
    roles: z.string().array().optional(),
    seniorityLevel: seniorityLevelSchema.optional(),
    skills: skillSchema.array().optional(),
    visibility: z.string().array().optional(),
});

export const userPaginationSchema = z.object({
    data: userSchema.array(),
    pagination: paginationSchema,
});

export type User = z.infer<typeof userSchema>;

export type UserMutation = Mutation<Omit<User, 'custom' | 'exists' | 'id'>> & {
    readonly custom?: Mutation<CustomAttributes> | undefined;
};
export type UserCreatePayload = {
    readonly aboutMe?: string;
    readonly courses?: readonly CourseHistoryMutation[];
    readonly custom?: CustomAttributes;
    readonly educations?: readonly EducationHistoryMutation[];
    readonly hardSoftSkills?: readonly HardSoftSkill[];
    readonly jobSeeking?: JobSeekingStatus;
    readonly jobTitle?: string;
    readonly languages?: readonly LanguageSkill[];
    readonly phoneNumber?: string;
    readonly projects?: readonly ProjectHistoryMutation[];
    readonly publications?: readonly PublicationHistoryMutation[];
    readonly relocationPreference?: RelocationPreference;
    readonly seniorityLevel?: number;
    readonly skillSource?: UserMeCreateSkillSource;
    readonly visibility?: readonly string[];
    readonly works?: readonly WorkHistoryMutation[];
};

export type PaginatedUsers = z.infer<typeof userPaginationSchema>;

const userHistoryWorkCreateSchema = z.object({
    companyName: z.string().max(100).min(1),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    department: z.string().max(100).min(1).optional(),
    description: z.string().max(5000).optional(),
    jobTitle: z.string().max(100).min(1),
    location: z.string().max(50).min(1).optional(),
    ongoing: z.boolean().optional(),
    skillIds: idSchema.array(),
});

const userHistoryEducationCreateSchema = z.object({
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    degree: z.string().max(100).min(1),
    description: z.string().max(5000).optional(),
    fieldOfStudy: z.string().max(100).min(1),
    ongoing: z.boolean().optional(),
    schoolName: z.string().max(100).min(1),
    skillIds: idSchema.array().optional(),
});

const userHistoryPublicationCreateSchema = z.object({
    date: z.string().optional(),
    description: z.string().max(5000).optional(),
    publicationName: z.string().max(100).min(1),
    publisher: z.string().max(100).min(1).optional(),
    skillIds: idSchema.array().optional(),
});

const userHistoryProjectCreateSchema = z.object({
    companyName: z.string().max(100).min(1),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    description: z.string().max(5000).optional(),
    location: z.string().max(50).min(1).optional(),
    ongoing: z.boolean().optional(),
    projectTitle: z.string().max(100).min(1),
    skillIds: idSchema.array().optional(),
});

const userHistoryCourseCreateSchema = z.object({
    courseTitle: z.string().max(100).min(1),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    description: z.string().max(5000).optional(),
    ongoing: z.boolean().optional(),
    school: z.string().max(100).min(1).optional(),
    skillIds: idSchema.array().optional(),
});

export const userMeCreateSkillSourceSchema = z.union([
    z.literal('preset_skills'),
    z.literal('cv_import'),
    z.literal('ai_generated'),
]);

export type UserMeCreateSkillSource = z.infer<typeof userMeCreateSkillSourceSchema>;

export const userMeSchema = userSchema.extend({
    exists: z.boolean(),
});

export type UserMe = z.infer<typeof userMeSchema>;

export const userMeCreateSchema = z
    .object({
        aboutMe: z.string().max(3000),
        courses: userHistoryCourseCreateSchema.array(),
        custom: customAttributesSchema,
        educations: userHistoryEducationCreateSchema.array(),
        hardSoftSkills: z.object({ id: idSchema, level: z.number() }).array(),
        jobSeeking: jobSeekingStatusSchema,
        jobTitle: z.string().max(128),
        languages: z.object({ id: idSchema, level: z.number() }).array(),
        phoneNumber: z.string().max(20),
        projects: userHistoryProjectCreateSchema.array(),
        publications: userHistoryPublicationCreateSchema.array(),
        relocationPreference: relocationPreferenceSchema,
        seniorityLevel: z.number(),
        skillSource: userMeCreateSkillSourceSchema,
        visibility: z.string().array(),
        works: userHistoryWorkCreateSchema.array(),
    })
    .partial();

export type UserMeCreate = z.infer<typeof userMeCreateSchema>;

export const profilePermissionsSchema = z.object({
    canUseControlRoom: z.boolean(),
    canUserCreateOwnProfile: z.boolean(),
    canUserDeleteOwnProfile: z.boolean(),
    canUserGetGdprExport: z.boolean(),
});

export type ProfilePermissions = z.infer<typeof profilePermissionsSchema>;

export const singleUserOptionsSchema = z.object({
    canEditDepartment: z.boolean(),
    canEditJobSeeking: z.boolean(),
    canEditRelocationPreference: z.boolean(),
    canEditSeniorityLevel: z.boolean(),
    canEditUser: z.boolean(),
    canReadUser: z.boolean(),
    customAttributes: customAttributeOptionsSchema.array(),
});

export type SingleUserOptions = z.infer<typeof singleUserOptionsSchema>;
